import { useState, useCallback } from 'react'
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore'
import { useAuth } from '../../../contexts/AuthContext'
import { getActiveDb } from '../../../config/firebase'
import type { IntentId } from './intents'

export function useOnboardingIntent() {
  const {
    userAccount,
    currentUser,
    reloadAccountFromFirestore,
    patchUserAccount,
    requestOnboardingIntentPicker,
    isTosModalOpen,
  } = useAuth()
  const [saving, setSaving] = useState(false)

  const shouldShow =
    !!currentUser &&
    !!userAccount &&
    !isTosModalOpen &&
    !!userAccount.compliance?.termsAcceptedAt &&
    !userAccount.onboardingCompleted &&
    !userAccount.onboardingSkipped &&
    !userAccount.onboardingIntent

  const pickIntent = useCallback(
    async (intent: IntentId) => {
      if (!currentUser) return
      setSaving(true)
      try {
        const ref = doc(getActiveDb(), 'accounts', currentUser.uid)
        await updateDoc(ref, {
          onboardingIntent: intent,
          onboardingIntentPickedAt: serverTimestamp(),
          onboardingCompleted: true,
        })
        await reloadAccountFromFirestore()
      } finally {
        setSaving(false)
      }
    },
    [currentUser, reloadAccountFromFirestore]
  )

  const dismiss = useCallback(async () => {
    if (!currentUser) return
    setSaving(true)
    try {
      const ref = doc(getActiveDb(), 'accounts', currentUser.uid)
      await updateDoc(ref, { onboardingSkipped: true })
      await reloadAccountFromFirestore()
    } finally {
      setSaving(false)
    }
  }, [currentUser, reloadAccountFromFirestore])

  const reset = useCallback(async () => {
    if (!currentUser) return
    setSaving(true)
    try {
      const ref = doc(getActiveDb(), 'accounts', currentUser.uid)
      await updateDoc(ref, {
        onboardingCompleted: false,
        onboardingSkipped: false,
        onboardingIntent: deleteField(),
        onboardingIntentPickedAt: deleteField(),
      })
      patchUserAccount({
        onboardingCompleted: false,
        onboardingSkipped: false,
        onboardingIntent: undefined,
        onboardingIntentPickedAt: undefined,
      })
      requestOnboardingIntentPicker()
      void reloadAccountFromFirestore()
    } finally {
      setSaving(false)
    }
  }, [currentUser, patchUserAccount, requestOnboardingIntentPicker, reloadAccountFromFirestore])

  return { shouldShow, saving, pickIntent, dismiss, reset }
}
